import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import {
  approveHygieneFinding,
  bulkHygiene,
  dismissHygieneFinding,
  getHygieneConfig,
  getHygieneFindings,
  type HygieneDetector,
  type HygieneFindingDto,
  runHygieneScan,
  serverErrorMessage,
  setContactArchived,
} from '../lib/api.js';
import { ChannelBadge } from './ChannelBadge.js';
import { EmptyState } from './states/EmptyState.js';
import { ErrorState } from './states/ErrorState.js';
import { LoadingState } from './states/LoadingState.js';

/**
 * batch-341 — the contact-hygiene review hub (web side of the batch-338/339
 * engine). Two tabs: JUNK (archive candidates, grouped by class with per-class
 * bulk actions) + ATTRIBUTION (a linked contact whose sender name / identities
 * look wrong). The DUPLICATES tab (the pre-existing identity-candidates UI) is
 * rendered by the caller and slotted in as `duplicatesPanel` — this component
 * owns only the two new panels + the tab frame, to keep churn off the candidates
 * code.
 *
 * Count-truth: each tab's pending count comes from the SAME
 * /contact-hygiene/findings list it renders (the badge equals the room). Privacy:
 * a finding renders the contact name + channel chips only; the `signals` jsonb is
 * dictionary words + counts (never ids), and we render exactly those.
 */

export type HygieneTab = 'duplicates' | 'junk' | 'attribution';

/** Human labels + one-line explanations for the junk classes. */
const JUNK_CLASS_META: Record<string, { label: string; explain: string }> = {
  generic_email_local: {
    label: 'Role / system email address',
    explain: 'Role/system email address (no reply expected)',
  },
  no_threads: { label: 'No conversation history', explain: 'No conversation history' },
  nameless_stranger: {
    label: 'Nameless stranger',
    explain: 'No name, single channel, never engaged',
  },
  inbound_only: {
    label: 'Inbound only',
    explain: 'They wrote; you never replied (>6 months)',
  },
};

/** Human labels for the attribution classes. */
const ATTRIBUTION_CLASS_META: Record<string, { label: string; explain: string }> = {
  name_mismatch: {
    label: 'Name mismatch',
    explain: "Sender name doesn't match the linked contact",
  },
  suspicious_merge: {
    label: 'Suspicious merge',
    explain: 'Multiple different names on one contact',
  },
  identity_pileup: {
    label: 'Identity pile-up',
    explain: 'Unusually many identities on one channel',
  },
};

function classMeta(detector: HygieneDetector, klass: string): { label: string; explain: string } {
  const meta = (detector === 'junk' ? JUNK_CLASS_META : ATTRIBUTION_CLASS_META)[klass];
  return meta ?? { label: klass.replaceAll('_', ' '), explain: '' };
}

/**
 * batch-341 — the "Contact hygiene" address-book badge count = the sum of the
 * three hub tabs (Duplicates + Junk + Attribution), so the number on the door
 * equals the items in the room. Returns `undefined` until BOTH sources have
 * loaded (never under-report a partial total). Pure so count-truth is unit-tested.
 */
export function hygieneBadgeCount(
  identityMatches: number | undefined,
  hygiene: { junk: number; attribution: number } | undefined,
): number | undefined {
  if (identityMatches === undefined || hygiene === undefined) return undefined;
  return identityMatches + hygiene.junk + hygiene.attribution;
}

export function ContactHygieneHub({
  onSelectContact,
  duplicatesPanel,
  duplicatesCount,
}: {
  onSelectContact: (id: string) => void;
  /** The pre-existing identity-candidates UI, rendered by the caller (unchanged). */
  duplicatesPanel: ReactNode;
  /** Pending duplicate candidates, for the Duplicates tab badge (count-truth). */
  duplicatesCount: number | undefined;
}) {
  const [tab, setTab] = useState<HygieneTab>('duplicates');
  const queryClient = useQueryClient();

  const config = useQuery({
    queryKey: ['contact-hygiene-config'],
    queryFn: ({ signal }) => getHygieneConfig({ signal }),
    staleTime: 60_000,
  });

  // Per-detector pending findings drive BOTH the panels and the tab badges, so a
  // tab count can't disagree with its list.
  const junk = useQuery({
    queryKey: ['contact-hygiene', 'junk'],
    queryFn: ({ signal }) => getHygieneFindings({ detector: 'junk', status: 'pending', signal }),
    refetchInterval: 60_000,
  });
  const attribution = useQuery({
    queryKey: ['contact-hygiene', 'attribution'],
    queryFn: ({ signal }) =>
      getHygieneFindings({ detector: 'attribution', status: 'pending', signal }),
    refetchInterval: 60_000,
  });

  const [scanErr, setScanErr] = useState<string | null>(null);
  const scan = useMutation({
    mutationFn: () => runHygieneScan(),
    onSuccess: () => {
      setScanErr(null);
      // A scan writes new findings — refresh every hygiene surface AND the
      // address-book badge (['contact-hygiene-summary'] is a distinct key, not a
      // child of ['contact-hygiene'], so it needs its own invalidate) + work-summary.
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contact-hygiene'] }),
        queryClient.invalidateQueries({ queryKey: ['contact-hygiene-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['work-summary'] }),
      ]);
    },
    onError: (e) => setScanErr(serverErrorMessage(e, 'scan failed')),
  });

  const enabled = config.data?.enabled;

  return (
    <div className="unified-detail-scroll" data-testid="contact-hygiene-hub">
      <div className="unified-page-card">
        <div className="unified-card-header">
          <div>
            <div className="unified-side-kicker">Address-book hygiene</div>
            <h2>Contact hygiene</h2>
          </div>
          <div className="unified-card-actions">
            {enabled === false ? null : (
              <button
                type="button"
                className="unified-btn"
                data-testid="hygiene-scan"
                onClick={() => scan.mutate()}
                disabled={scan.isPending || !config.isSuccess}
              >
                {scan.isPending ? 'Scanning…' : 'Scan now'}
              </button>
            )}
          </div>
        </div>
        <p>
          Review contacts the hygiene engine flagged as junk (archive candidates) or mis-attributed.
          Archiving a contact is reversible from the address book.
        </p>
        {/* Honest inert notice when the engine is off in operator config (mirrors the
            locked-hub copy elsewhere). We still show any existing findings below. */}
        {enabled === false ? (
          <p className="unified-scan-result" data-testid="hygiene-inert-notice">
            The contact-hygiene engine is off in your operator configuration, so no new scan can
            run. Any findings from a previous scan are still shown below.
          </p>
        ) : null}
        {scan.isError ? (
          <p className="unified-scan-result" data-testid="hygiene-scan-error">
            {scanErr ?? 'Scan failed — try again.'}
          </p>
        ) : null}
      </div>

      <div className="queue-tabs hygiene-tabs" role="tablist" aria-label="Contact hygiene sections">
        <HygieneTabButton
          id="duplicates"
          label="Duplicates"
          count={duplicatesCount}
          active={tab === 'duplicates'}
          onSelect={setTab}
        />
        <HygieneTabButton
          id="junk"
          label="Junk"
          count={junk.data?.counts.total}
          active={tab === 'junk'}
          onSelect={setTab}
        />
        <HygieneTabButton
          id="attribution"
          label="Attribution"
          count={attribution.data?.counts.total}
          active={tab === 'attribution'}
          onSelect={setTab}
        />
      </div>

      {tab === 'duplicates' ? (
        <div data-testid="hygiene-duplicates-content">{duplicatesPanel}</div>
      ) : null}
      {tab === 'junk' ? (
        <>
          <HygieneFindingsPanel detector="junk" query={junk} onSelectContact={onSelectContact} />
          <HygieneAutoArchivedSection />
        </>
      ) : null}
      {tab === 'attribution' ? (
        <HygieneFindingsPanel
          detector="attribution"
          query={attribution}
          onSelectContact={onSelectContact}
        />
      ) : null}
    </div>
  );
}

function HygieneTabButton({
  id,
  label,
  count,
  active,
  onSelect,
}: {
  id: HygieneTab;
  label: string;
  count: number | undefined;
  active: boolean;
  onSelect: (t: HygieneTab) => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={`hygiene-tab-${id}`}
      className={active ? 'active' : ''}
      onClick={() => onSelect(id)}
    >
      {label}
      {count && count > 0 ? <span className="unified-count-pill">{count}</span> : null}
    </button>
  );
}

/**
 * A short human outcome line for a bulk action, surfacing how many were applied
 * of matched and (for junk archive) how many were skipped as protected.
 */
function bulkResultNote(
  detector: HygieneDetector,
  action: 'approve' | 'dismiss',
  r: { applied: number; skipped_protected: number; matched: number },
): string {
  if (action === 'dismiss') {
    return `Dismissed ${r.applied} of ${r.matched} findings.`;
  }
  const verb = detector === 'junk' ? 'Archived' : 'Marked reviewed';
  const base = `${verb} ${r.applied} of ${r.matched}.`;
  return r.skipped_protected > 0
    ? `${base} ${r.skipped_protected} were protected and skipped.`
    : base;
}

type FindingsQuery = ReturnType<typeof useQuery<Awaited<ReturnType<typeof getHygieneFindings>>>>;

function HygieneFindingsPanel({
  detector,
  query,
  onSelectContact,
}: {
  detector: HygieneDetector;
  query: FindingsQuery;
  onSelectContact: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [actionErr, setActionErr] = useState<string | null>(null);
  // An informational (non-error) outcome line: how many archived vs. were skipped
  // as protected. The server returns these counts precisely so the operator isn't
  // left thinking a protected-skip was a silent no-op or a failure.
  const [actionNote, setActionNote] = useState<string | null>(null);

  const invalidateAll = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['contact-hygiene'] }),
      queryClient.invalidateQueries({ queryKey: ['contact-hygiene-summary'] }),
      queryClient.invalidateQueries({ queryKey: ['work-summary'] }),
      // An approve on junk archives the contact — the address book changes too.
      queryClient.invalidateQueries({ queryKey: ['contacts'] }),
    ]);

  const approve = useMutation({
    mutationFn: (id: string) => approveHygieneFinding(id),
    onSuccess: (data) => {
      setActionErr(null);
      setActionNote(
        data.outcome === 'skipped_protected'
          ? 'Not archived — this contact is protected (a favorite or scheduled for check-ins). Un-protect it in the address book first, or dismiss the finding.'
          : null,
      );
      return invalidateAll();
    },
    onError: (e) => {
      setActionNote(null);
      setActionErr(serverErrorMessage(e, 'action failed'));
    },
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => dismissHygieneFinding(id),
    onSuccess: () => {
      setActionErr(null);
      setActionNote(null);
      return invalidateAll();
    },
    onError: (e) => {
      setActionNote(null);
      setActionErr(serverErrorMessage(e, 'action failed'));
    },
  });
  const bulk = useMutation({
    mutationFn: (vars: { class: string; action: 'approve' | 'dismiss' }) =>
      bulkHygiene({ detector, class: vars.class, action: vars.action }),
    onSuccess: (data, vars) => {
      setActionErr(null);
      setActionNote(bulkResultNote(detector, vars.action, data));
      return invalidateAll();
    },
    onError: (e) => {
      setActionNote(null);
      setActionErr(serverErrorMessage(e, 'bulk action failed'));
    },
  });

  if (query.isPending) return <LoadingState label="Loading hygiene findings" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const items = query.data.items;
  if (items.length === 0) {
    return (
      <EmptyState
        title={detector === 'junk' ? 'No junk findings pending' : 'No attribution findings pending'}
        hint="Run 'Scan now' above to re-check contacts for new findings."
      />
    );
  }

  // Group by class so we can offer a per-class bulk action.
  const byClass = new Map<string, HygieneFindingDto[]>();
  for (const f of items) {
    const arr = byClass.get(f.class) ?? [];
    arr.push(f);
    byClass.set(f.class, arr);
  }

  const busyId =
    (approve.isPending ? approve.variables : undefined) ??
    (dismiss.isPending ? dismiss.variables : undefined);

  return (
    <div data-testid={`hygiene-panel-${detector}`}>
      {actionErr ? (
        <p className="unified-scan-result" data-testid="hygiene-action-error">
          {actionErr}
        </p>
      ) : null}
      {actionNote ? (
        <p className="unified-side-note" data-testid="hygiene-action-note">
          {actionNote}
        </p>
      ) : null}
      {[...byClass.entries()].map(([klass, group]) => (
        <HygieneClassGroup
          key={klass}
          detector={detector}
          klass={klass}
          group={group}
          onSelectContact={onSelectContact}
          onApprove={(id) => approve.mutate(id)}
          onDismiss={(id) => dismiss.mutate(id)}
          onBulk={(action) => bulk.mutate({ class: klass, action })}
          bulkPending={bulk.isPending && bulk.variables?.class === klass}
          busyId={busyId}
        />
      ))}
    </div>
  );
}

function HygieneClassGroup({
  detector,
  klass,
  group,
  onSelectContact,
  onApprove,
  onDismiss,
  onBulk,
  bulkPending,
  busyId,
}: {
  detector: HygieneDetector;
  klass: string;
  group: HygieneFindingDto[];
  onSelectContact: (id: string) => void;
  onApprove: (id: string) => void;
  onDismiss: (id: string) => void;
  onBulk: (action: 'approve' | 'dismiss') => void;
  bulkPending: boolean;
  busyId: string | undefined;
}) {
  const meta = classMeta(detector, klass);
  const n = group.length;
  // Two-step confirm for the class bulk (states the count). Junk's approve archives,
  // so it's a real mutation; attribution's approve just marks reviewed.
  const [confirm, setConfirm] = useState<'approve' | 'dismiss' | null>(null);
  const isJunk = detector === 'junk';
  const bulkApproveLabel = isJunk ? `Archive all ${n}` : `Mark all ${n} reviewed`;

  return (
    <div className="unified-page-card hygiene-card" data-testid={`hygiene-class-${klass}`}>
      <div className="unified-card-header">
        <div>
          <h3 className="hygiene-card-title">{meta.label}</h3>
          {meta.explain ? <p className="unified-side-note">{meta.explain}</p> : null}
        </div>
        <div className="unified-card-actions">
          <span className="unified-count-pill">{n}</span>
          {confirm ? (
            <>
              <span className="unified-side-note" data-testid={`hygiene-bulk-confirm-${klass}`}>
                {confirm === 'approve'
                  ? isJunk
                    ? `Archive ${n} contacts?`
                    : `Mark ${n} reviewed?`
                  : `Dismiss ${n} findings?`}
              </span>
              <button
                type="button"
                className="unified-btn"
                data-testid={`hygiene-bulk-go-${klass}`}
                disabled={bulkPending}
                onClick={() => {
                  onBulk(confirm);
                  setConfirm(null);
                }}
              >
                {bulkPending ? 'Working…' : 'Confirm'}
              </button>
              <button type="button" className="unified-btn" onClick={() => setConfirm(null)}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="unified-btn"
                data-testid={`hygiene-bulk-approve-${klass}`}
                disabled={bulkPending}
                onClick={() => setConfirm('approve')}
              >
                {bulkApproveLabel}
              </button>
              <button
                type="button"
                className="unified-btn"
                data-testid={`hygiene-bulk-dismiss-${klass}`}
                disabled={bulkPending}
                onClick={() => setConfirm('dismiss')}
              >
                Dismiss all {n}
              </button>
            </>
          )}
        </div>
      </div>

      <ul className="unified-card-list">
        {group.map((f) => (
          <HygieneFindingRow
            key={f.id}
            finding={f}
            approveLabel={isJunk ? 'Archive' : 'Mark reviewed'}
            onSelectContact={onSelectContact}
            onApprove={() => onApprove(f.id)}
            onDismiss={() => onDismiss(f.id)}
            pending={busyId === f.id}
          />
        ))}
      </ul>
    </div>
  );
}

function HygieneFindingRow({
  finding,
  approveLabel,
  onSelectContact,
  onApprove,
  onDismiss,
  pending,
}: {
  finding: HygieneFindingDto;
  approveLabel: string;
  onSelectContact: (id: string) => void;
  onApprove: () => void;
  onDismiss: () => void;
  pending: boolean;
}) {
  const { contact } = finding;
  return (
    <li className="unified-candidate-row" data-testid="hygiene-finding-row">
      <div className="unified-candidate-sides">
        <button
          type="button"
          className="unified-candidate-side"
          onClick={() => onSelectContact(contact.id)}
        >
          <strong>{contact.name ?? 'Unknown'}</strong>
          <span>
            {contact.channels.map((channel) => (
              <ChannelBadge key={channel} channel={channel} size="xs" />
            ))}
          </span>
        </button>
        <span className="unified-count-pill">{finding.confidence}%</span>
      </div>
      <div className="unified-reason-row" data-testid="hygiene-signals">
        {formatSignals(finding.signals)}
      </div>
      <div className="unified-row-actions">
        <button type="button" disabled={pending} onClick={onApprove}>
          {approveLabel}
        </button>
        <button type="button" disabled={pending} onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </li>
  );
}

/**
 * The FINITE set of signal keys the hygiene detectors emit
 * (packages/domain/src/contact-hygiene.ts) — dictionary WORD arrays
 * (generic_locals/channels) + numeric/boolean counts, NEVER ids or raw
 * names. This is an ALLOWLIST, not documentation: `formatSignals` renders a
 * key ONLY if it appears here, so a future detector, a backfill, or any
 * unexpected value that lands a raw string in `signals` is dropped rather than
 * printed verbatim (defense-in-depth for the PII boundary — the renderer is the
 * last line before display and must not trust the producer). Adding a new signal
 * is a deliberate one-line change here.
 */
const RENDERABLE_SIGNAL_KEYS = new Set<string>([
  'identity_count',
  'generic_locals',
  'thread_count',
  'channels',
  'channel',
  'has_outbound',
  'last_inbound_days_ago',
  'sender_token_count',
  'contact_token_count',
  'shared_tokens',
  'name_like_handles',
  'disjoint_pair',
]);

/**
 * Render the `signals` jsonb as human-readable evidence chips. Only allowlisted
 * keys (RENDERABLE_SIGNAL_KEYS) render; arrays are joined and word-spaced, counts
 * shown as `key: value`, booleans as a bare label. An unrecognized key is skipped
 * entirely so a stray raw value can never reach the DOM.
 */
function formatSignals(signals: Record<string, unknown> | null): ReactNode {
  if (!signals) return null;
  const parts: string[] = [];
  for (const [key, value] of Object.entries(signals)) {
    if (!RENDERABLE_SIGNAL_KEYS.has(key)) continue;
    const label = key.replaceAll('_', ' ');
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      parts.push(`${label}: ${value.map((v) => String(v)).join(', ')}`);
    } else if (typeof value === 'boolean') {
      if (value) parts.push(label);
    } else if (value !== null && value !== undefined) {
      parts.push(`${label}: ${String(value)}`);
    }
  }
  return parts.map((p) => <span key={p}>{p}</span>);
}

/**
 * batch-341 — the "Recently auto-archived" section. Junk findings the WORKER
 * auto-applied (status=auto_applied) with a one-click Restore per row (reuses the
 * contact archive endpoint). Rendered by the caller under the Junk context so it
 * stays out of the main review flow.
 */
export function HygieneAutoArchivedSection() {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['contact-hygiene', 'auto-applied'],
    queryFn: ({ signal }) => getHygieneFindings({ status: 'auto_applied', signal }),
    // Only fetch once expanded — this is a background reference list, not a live queue.
    enabled: expanded,
    refetchInterval: false,
  });

  const restore = useMutation({
    mutationFn: (contactId: string) => setContactArchived(contactId, false),
    onSuccess: () => {
      setErr(null);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contact-hygiene'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
      ]);
    },
    onError: (e) => setErr(serverErrorMessage(e, 'restore failed')),
  });

  const total = query.data?.items.length;

  return (
    <div className="unified-page-card hygiene-card" data-testid="hygiene-auto-archived">
      <button
        type="button"
        className="unified-btn"
        aria-expanded={expanded}
        data-testid="hygiene-auto-archived-toggle"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? '▾' : '▸'} Recently auto-archived{total !== undefined ? ` (${total})` : ''}
      </button>
      {expanded ? (
        query.isPending ? (
          <LoadingState label="Loading auto-archived" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data.items.length === 0 ? (
          <p className="unified-side-note">Nothing has been auto-archived.</p>
        ) : (
          <>
            {err ? (
              <p className="unified-scan-result" data-testid="hygiene-restore-error">
                {err}
              </p>
            ) : null}
            <ul className="unified-card-list">
              {query.data.items.map((f) => (
                <li
                  key={f.id}
                  className="unified-candidate-row"
                  data-testid="hygiene-auto-archived-row"
                >
                  <div className="unified-candidate-sides">
                    <strong>{f.contact.name ?? 'Unknown'}</strong>
                    <span>
                      {f.contact.channels.map((channel) => (
                        <ChannelBadge key={channel} channel={channel} size="xs" />
                      ))}
                    </span>
                    <span className="unified-count-pill">{classMeta('junk', f.class).label}</span>
                  </div>
                  <div className="unified-row-actions">
                    {f.contact.archived ? (
                      <button
                        type="button"
                        data-testid="hygiene-restore"
                        disabled={restore.isPending && restore.variables === f.contact.id}
                        onClick={() => restore.mutate(f.contact.id)}
                      >
                        Restore
                      </button>
                    ) : (
                      // Live archived state (JOIN to contacts.archived_at): once
                      // restored, the row reflects it instead of offering an inert
                      // Restore that would re-un-archive an already-active contact.
                      <span className="unified-side-note" data-testid="hygiene-restored">
                        Restored ✓
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )
      ) : null}
    </div>
  );
}
